import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Clock, XCircle, Wallet } from 'lucide-react';
import { passAdmin } from '../../services/passApi';

const TABS = [
  ['pending', 'Pendientes'],
  ['approved', 'Aprobados'],
  ['paid', 'Pagados'],
  ['rejected', 'Rechazados'],
  ['', 'Todos'],
];

export default function PassPayouts() {
  const [tab, setTab] = useState('pending');
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function load() {
    setLoading(true);
    try {
      const res = await passAdmin.listPayouts(tab ? { status: tab } : {});
      setPayouts(res.data.payouts || []);
    } catch (err) {
      toast.error('Error cargando retiros');
    } finally {
      setLoading(false);
    }
  }

  async function decide(payoutId, status) {
    setBusyId(payoutId);
    try {
      await passAdmin.decidePayout(payoutId, { status });
      toast.success(`Marcado como ${status}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <DashboardLayout>
      <div className="px-6 py-6">
        <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2 mb-2">
          <Wallet className="w-6 h-6 text-teal-600" /> Retiros Pass — Afiliados
        </h1>
        <p className="text-sm text-slate-500 mb-5">
          Aprueba o marca como pagados los retiros solicitados por los afiliados.
        </p>

        <div className="flex gap-2 mb-4 overflow-x-auto">
          {TABS.map(([v, l]) => (
            <button
              key={v || 'all'}
              onClick={() => setTab(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                tab === v ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : payouts.length === 0 ? (
            <p className="text-center text-slate-500 py-10">Sin retiros para este filtro.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Afiliado</th>
                  <th className="text-right px-4 py-2">Monto</th>
                  <th className="text-left px-4 py-2">Método</th>
                  <th className="text-left px-4 py-2">Cuenta</th>
                  <th className="text-left px-4 py-2">Solicitado</th>
                  <th className="text-left px-4 py-2">Estado</th>
                  <th className="text-right px-4 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {payouts.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-2 font-mono text-xs">{p.affiliate_id}</td>
                    <td className="px-4 py-2 text-right font-bold text-slate-900">${Number(p.amount).toFixed(2)}</td>
                    <td className="px-4 py-2">{p.method || '—'}</td>
                    <td className="px-4 py-2">{p.destination?.account || '—'}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {new Date(p.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex gap-1">
                        {p.status === 'pending' && (
                          <>
                            <ActionBtn label="Aprobar" onClick={() => decide(p.id, 'approved')} busy={busyId === p.id} variant="blue" />
                            <ActionBtn label="Rechazar" onClick={() => decide(p.id, 'rejected')} busy={busyId === p.id} variant="red" />
                          </>
                        )}
                        {(p.status === 'pending' || p.status === 'approved') && (
                          <ActionBtn label="Marcar pagado" onClick={() => decide(p.id, 'paid')} busy={busyId === p.id} variant="green" />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: 'Pendiente', icon: Clock, cls: 'bg-amber-50 text-amber-700' },
    approved: { label: 'Aprobado', icon: CheckCircle2, cls: 'bg-blue-50 text-blue-700' },
    paid: { label: 'Pagado', icon: CheckCircle2, cls: 'bg-green-50 text-green-700' },
    rejected: { label: 'Rechazado', icon: XCircle, cls: 'bg-red-50 text-red-700' },
  };
  const m = map[status] || { label: status, icon: Clock, cls: 'bg-slate-100 text-slate-700' };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function ActionBtn({ label, onClick, busy, variant }) {
  const colors = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    red: 'bg-red-600 hover:bg-red-700',
    green: 'bg-green-600 hover:bg-green-700',
  };
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-white text-xs font-semibold disabled:opacity-60 ${colors[variant] || 'bg-slate-700'}`}
    >
      {busy && <Loader2 className="w-3 h-3 animate-spin" />} {label}
    </button>
  );
}
